import React, { useState, useEffect } from 'react';
import { Card, Row, Col, message, Tag, Button, Spin, Alert } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import api from '../services/api';

const PlansList = ({ 
  plans = [], 
  user = {}, 
  onPlanUpdate = () => {} 
}) => {
 
  const [loading, setLoading] = useState(false);
  const [activePlans, setActivePlans] = useState([]);
  const [paymentProcessing, setPaymentProcessing] = useState(null);
  // Fetch user's active plans on component mount
useEffect(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const paymentStatus = urlParams.get("payment");
  const transactionId = urlParams.get("transactionId");

  if (paymentStatus && transactionId) {
    // Check if already handled
    const handledKey = `payment_handled_${transactionId}`;
    const alreadyHandled = sessionStorage.getItem(handledKey);
    
    if (!alreadyHandled) {
      // Mark as handled FIRST
      sessionStorage.setItem(handledKey, "true");
      
      // Clear URL immediately
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Call handler
      handlePaymentReturn(paymentStatus, transactionId, urlParams);
    } else {
      // Already handled, just clear URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }

  fetchActivePlans();
}, []);




  const fetchActivePlans = async () => {
    try {
      const response = await api.get('/user/active-plans');
      setActivePlans(response.data.plans || []);
    } catch (error) {
      console.error('Error fetching active plans:', error);
      setActivePlans([]);
    }
  };

const handlePaymentReturn = (paymentStatus, transactionId, urlParams) => {
  const reason = urlParams.get("reason");
  const planActivated = urlParams.get("planActivated");

  switch (paymentStatus) {
    case "success":
      message.success({
        content: planActivated === "true"
          ? `Payment successful! Your plan has been activated. Transaction ID: ${transactionId}`
          : `Payment completed! Transaction ID: ${transactionId}`,
        duration: 6,
        key: `payment-${transactionId}`, // Add unique key
      });
      fetchActivePlans();
      if (onPlanUpdate) onPlanUpdate();
      break;

    case "pending":
      message.warning({
        content: `Payment is being processed. Transaction ID: ${transactionId}`,
        duration: 8,
        key: `payment-${transactionId}`,
      });
      setTimeout(fetchActivePlans, 30000);
      break;

    case "failed":
      message.error({
        content: `Payment failed: ${decodeURIComponent(reason || "Payment was not completed successfully")}.`,
        duration: 8,
        key: `payment-${transactionId}`,
      });
      break;

    case "error":
      const errorMsg = urlParams.get("message") || "An error occurred during payment processing.";
      message.error({
        content: `Payment error: ${errorMsg.replace(/_/g, " ")}`,
        duration: 8,
        key: `payment-${transactionId}`, // Add unique key
      });
      break;

    default:
      break;
  }
};



  const handlePurchase = async (plan) => {
    if (!plan) return;
    
    setLoading(true);
    setPaymentProcessing(plan.id);
    
    try {
      console.log('Initiating payment for plan:', plan);
      
      // Initiate payment
      const response = await api.post('/user/initiate-payment', { 
        planId: plan.id
      });
      
      console.log('Payment initiation response:', response.data);
      
      if (response.data.success) {
        // Store transaction info in sessionStorage for tracking
        sessionStorage.setItem('pendingTransaction', JSON.stringify({
          transactionId: response.data.data.transactionId,
          orderId: response.data.data.orderId,
          planId: plan.id,
          planName: plan.plan_name,
          amount: plan.price,
          timestamp: Date.now()
        }));
        
        // Show redirect message
        message.loading({
          content: 'Redirecting to PhonePe payment gateway...',
          duration: 2
        });
        
        console.log('Redirecting to PhonePe:', response.data.data.redirectUrl);
        
        // Small delay before redirect for better UX
        setTimeout(() => {
  
          window.location.href = response.data.data.redirectUrl;
        }, 1000);
        
      } else {
        console.error('Payment initiation failed:', response.data);
        message.error({
          content: response.data.message || 'Failed to initiate payment',
          duration: 5
        });
        setPaymentProcessing(null);
      }
    } catch (error) {
      console.error('Payment initiation error:', error);
      
      let errorMsg = 'Failed to initiate payment. Please try again.';
      
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.response?.status === 404) {
        errorMsg = 'Plan not found or inactive.';
      } else if (error.response?.status === 400) {
        errorMsg = error.response.data?.message || 'Invalid request. Please check your plan selection.';
      } else if (error.response?.status >= 500) {
        errorMsg = 'Server error. Please try again later.';
      }
      
      message.error({
        content: errorMsg,
        duration: 6
      });
      setPaymentProcessing(null);
    } finally {
      setLoading(false);
    }
  };

  // Check if plan is already active
  const isPlanActive = (planId) => {
    return activePlans.some(plan => plan.plan_id === planId);
  };

  // Get expiry date for active plan
  const getExpiryDate = (planId) => {
    const plan = activePlans.find(p => p.plan_id === planId);
    return plan ? new Date(plan.expiry_date).toLocaleDateString() : null;
  };

  // Get active plan details
  const getActivePlanDetails = (planId) => {
    return activePlans.find(p => p.plan_id === planId);
  };

  const formatPrice = (price) => {
    return typeof price === 'string' ? parseFloat(price) : price;
  };

  return (
    <div className="plans-container">
      <div style={{ marginBottom: 20 }}>
        <h3>Available Plans for {user.type || 'User'}</h3>
        
        {activePlans.length > 0 && (
          <Alert
            message={`You have ${activePlans.length} active plan${activePlans.length > 1 ? 's' : ''}`}
            type="info"
            showIcon
            icon={<CheckCircleOutlined />}
            style={{ marginBottom: 16 }}
          />
        )}
      </div>
      
      {plans.length === 0 ? (
        <Alert
          message="No Plans Available"
          description="No plans are currently available for your user type."
          type="info"
          showIcon
        />
      ) : (
        <Row gutter={[16, 16]}>
          {plans.map(plan => {
            const isActive = isPlanActive(plan.id);
            const activePlanDetails = getActivePlanDetails(plan.id);
            const isProcessing = paymentProcessing === plan.id;
            
            return (
              <Col key={plan.id} xs={24} sm={12} lg={8}>
                <Card 
                  title={
                    <div>
                      {plan.plan_name || 'Unnamed Plan'}
                      {isActive && (
                        <Tag color="success" style={{ marginLeft: 8 }}>
                          ACTIVE
                        </Tag>
                      )}
                    </div>
                  }
                  style={{ 
                    background: isActive ? '#f6ffed' : '#f9f9f9',
                    minHeight: '400px'
                  }}
                >
                  {/* Active Plan Status */}
                  {isActive && activePlanDetails && (
                    <div style={{ marginBottom: 16 }}>
                      <Tag color="green">
                        Active until {getExpiryDate(plan.id)}
                      </Tag>
                      <br />
                      <small style={{ color: '#52c41a' }}>
                        Purchased on {new Date(activePlanDetails.purchase_date).toLocaleDateString()}
                      </small>
                    </div>
                  )}
                  
                  {/* Price Display */}
                  <div style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '16px', color: '#1890ff' }}>
                    ₹{formatPrice(plan.price)} 
                    <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#666' }}>
                      / {plan.validity_days} days
                    </span>
                  </div>
                  
                  {/* Plan Description */}
                  <div style={{ marginBottom: '16px', minHeight: '60px' }}>
                    {plan.content || plan.description || 'No description available.'}
                  </div>
                  
                  {/* Plan Meta Information */}
                  <div className="plan-meta" style={{ fontSize: '12px', color: '#666', marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span>Status:</span>
                      <span style={{ fontWeight: 500 }}>{plan.status || 'N/A'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span>Validity:</span>
                      <span style={{ fontWeight: 500 }}>{plan.validity_days} days</span>
                    </div>
          	           {/* {plan.expiration_date && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Available until:</span>
                        <span style={{ fontWeight: 500 }}>
                          {new Date(plan.expiration_date).toLocaleDateString()}
                        </span>
                      </div>
                    )} */}
                  </div>

                  {/* Action Button */}
                  <div style={{ textAlign: 'center', marginTop: 'auto' }}>
                    {isActive ? (
                      <Button 
                        type="default" 
                        disabled 
                        size="large"
                        style={{ width: '100%', border: 'none', background: 'transparent' }}
                      >
                        ✓ Active Plan
                      </Button>
                    ) : (
                      <Button 
                        type="primary" 
                        size="large"
                        style={{ width: '100%' }}
                        onClick={() => handlePurchase(plan)}
                        loading={isProcessing}
                        disabled={isProcessing}
                      >
                        {isProcessing ? (
                          <span>
                            <Spin size="small" style={{ marginRight: 8 }} />
                            Redirecting to PhonePe...
                          </span>
                        ) : (
                          `Pay Now`
                        )}
                      </Button>
                    )}
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </div>
  );
};

PlansList.propTypes = {
  plans: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      plan_name: PropTypes.string.isRequired,
      validity_days: PropTypes.number.isRequired,
      price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      content: PropTypes.string,
      description: PropTypes.string,
      expiration_date: PropTypes.string,
      status: PropTypes.string
    })
  ),
  user: PropTypes.object,
  onPlanUpdate: PropTypes.func
};

PlansList.defaultProps = {
  plans: [],
  user: {},
  onPlanUpdate: () => {}
};

export default PlansList;